import React, { createContext, useReducer, useContext } from 'react';
import { useLocalStorage } from '@dentalux/ui-library-core/hooks';
import { vitaminDUpsellInformation } from 'src/@types/Booking';
import { LocalStorage } from 'src/@types/LocalStorage';
import { BookingScreenType } from 'src/screens/Appointments/Management/types';
export let ActionTypes = /*#__PURE__*/function (ActionTypes) {
  ActionTypes["setBookingIds"] = "setBookingIds";
  ActionTypes["setManipulatedAppointment"] = "setManipulatedAppointment";
  ActionTypes["setRecommendations"] = "setRecommendations";
  ActionTypes["setUpsellReferenceId"] = "setUpsellReferenceId";
  ActionTypes["setDefaultBookingsContext"] = "setDefaultBookingsContext";
  ActionTypes["setRecommendationDefaultBookingsContext"] = "setRecommendationDefaultBookingsContext";
  ActionTypes["setSlot"] = "setSlot";
  ActionTypes["setAppointmentBundle"] = "setAppointmentBundle";
  ActionTypes["setBookingScreenType"] = "setBookingScreenType";
  ActionTypes["setTreatmentTitle"] = "setTreatmentTitle";
  ActionTypes["setPainDetails"] = "setPainDetails";
  ActionTypes["setMarketingParams"] = "setMarketingParams";
  ActionTypes["setUpsellInformation"] = "setUpsellInformation";
  return ActionTypes;
}({});
const BookingsContext = /*#__PURE__*/createContext(undefined);
const BookingsDispatchContext = /*#__PURE__*/createContext(undefined);
export const defaultBookingsState = {
  clinicReferenceId: null,
  practitionerReferenceId: null,
  practitionerReferenceIds: [],
  appointmentBundleReferenceId: null,
  crmRecommendationId: null,
  recallReferenceId: null,
  recommendationGapfillerId: null,
  treatmentBundleReferenceId: null,
  upsellTreatmentBundleReferenceId: null,
  appointmentBundle: null,
  bookingScreenType: BookingScreenType.Default,
  treatmentTitle: null,
  recommendation: null,
  upsellReferenceId: null,
  upsellInformation: undefined,
  manipulatedAppointment: null,
  painDetails: null,
  marketingParams: null,
  slot: {
    duration: 0,
    start: ''
  }
};
const bookingsReducer = persist => (state, action) => {
  const finalize = nextState => {
    persist(nextState);
    return nextState;
  };
  switch (action.type) {
    case ActionTypes.setDefaultBookingsContext:
      return finalize({
        ...defaultBookingsState
      });
    case ActionTypes.setManipulatedAppointment:
      return finalize({
        ...state,
        manipulatedAppointment: action.payload
      });
    case ActionTypes.setRecommendationDefaultBookingsContext:
      return finalize({
        ...defaultBookingsState,
        recommendation: state.recommendation
      });
    case ActionTypes.setBookingIds:
      return finalize({
        ...state,
        ...action.payload
      });
    case ActionTypes.setRecommendations:
      return finalize({
        ...state,
        recommendation: action.payload
      });
    case ActionTypes.setUpsellReferenceId:
      return finalize({
        ...state,
        upsellReferenceId: action.payload
      });
    case ActionTypes.setSlot:
      return finalize({
        ...state,
        slot: {
          ...state.slot,
          ...action.payload
        }
      });
    case ActionTypes.setAppointmentBundle:
      return finalize({
        ...state,
        appointmentBundle: action.payload
      });
    case ActionTypes.setBookingScreenType:
      return finalize({
        ...state,
        bookingScreenType: action.payload
      });
    case ActionTypes.setTreatmentTitle:
      return finalize({
        ...state,
        treatmentTitle: action.payload
      });
    case ActionTypes.setPainDetails:
      return finalize({
        ...state,
        painDetails: action.payload
      });
    case ActionTypes.setMarketingParams:
      return finalize({
        ...state,
        marketingParams: action.payload
      });
    case ActionTypes.setUpsellInformation:
      return finalize({
        ...state,
        upsellInformation: action.payload
      });
    default:
      throw new Error(`Unhandled action`);
  }
};
export const BookingsProvider = ({
  children
}) => {
  const [value, setValue] = useLocalStorage(LocalStorage.Bookings, defaultBookingsState);
  const [state, dispatch] = useReducer(bookingsReducer(setValue), {
    ...value
  });
  return /*#__PURE__*/React.createElement(BookingsContext.Provider, {
    value: state
  }, /*#__PURE__*/React.createElement(BookingsDispatchContext.Provider, {
    value: dispatch
  }, children));
};
export const useBookings = () => {
  const context = useContext(BookingsContext);
  if (context === undefined) {
    throw new Error('useBookings must be used within a BookingsProvider');
  }
  return context;
};
export const useBookingsDispatch = () => {
  const context = useContext(BookingsDispatchContext);
  if (context === undefined) {
    throw new Error('useBookingsDispatch must be used within a BookingsProvider');
  }
  return context;
};
export const setBookingIds = payload => ({
  type: ActionTypes.setBookingIds,
  payload
});
export const setManipulatedAppointment = payload => ({
  type: ActionTypes.setManipulatedAppointment,
  payload
});
export const setDefaultBookingContext = () => ({
  type: ActionTypes.setDefaultBookingsContext
});
export const setRecommendationDefaultBookingContext = () => ({
  type: ActionTypes.setRecommendationDefaultBookingsContext
});
export const setAppointmentBundle = payload => ({
  type: ActionTypes.setAppointmentBundle,
  payload
});
export const setRecommendation = payload => ({
  type: ActionTypes.setRecommendations,
  payload
});
export const setUpsellReferenceId = payload => ({
  type: ActionTypes.setUpsellReferenceId,
  payload
});
export const setSlot = payload => ({
  type: ActionTypes.setSlot,
  payload
});
export const setBookingScreenType = payload => ({
  type: ActionTypes.setBookingScreenType,
  payload
});
export const setTreatmentTitle = payload => ({
  type: ActionTypes.setTreatmentTitle,
  payload
});
export const setPainDetails = payload => ({
  type: ActionTypes.setPainDetails,
  payload
});
export const setMarketingParams = payload => ({
  type: ActionTypes.setMarketingParams,
  payload
});
export const setVitaminDUpsellInformation = () => ({
  type: ActionTypes.setUpsellInformation,
  payload: vitaminDUpsellInformation
});
export const clearVitaminDUpsellInformation = () => ({
  type: ActionTypes.setUpsellInformation,
  payload: undefined
});